import { LoggingLevel } from '@augloop/interfaces';
import { Log, LogCategory, LoggingAggregator, Operation as LoggingOperation, getNumLoggers } from '@augloop/logging';
import { TelemetryLogger } from './telemetry-logger';
import { WebSocketWorker } from './websocket/websocket-worker';
import { WebSocketSessionManager } from './websocket/websocket-session-manager';
import { SessionProxy } from './session-proxy';
import { Utils } from './utils';
import { SessionStats } from './websocket/isession-manager';
import { SessionInitializer } from './websocket/session-initializer';
import { TokenRefreshManager } from './websocket/token-refresh-manager';
import { LocalWorkflowExecutionManager } from './local-workflow-execution-manager';
import { RoutingAnnotationResultsProcessor } from './annotation-processing/routing-annotation-results-processor';
import { EmptySessionManager } from './websocket/empty-session-manager';
import { NetworkMode } from '@augloop/session-protocol-internal';
import { parseFlights } from '@augloop/workflow-flights';
const defaultAggregatorIntervalSec = 30;
export const getVersion = () => {
    /* eslint-disable-next-line @typescript-eslint/no-require-imports, node/no-missing-require */
    return require('./version.json').version;
};
export class ClientRuntime {
    constructor(workerFactory, sessionManagerFactory, sessionFactory) {
        this.sessionsByDocSessionId = new Map();
        this.isDeltaGeneratorEnabled = false;
        this.disableSyncDeltaSending = false;
        this.hasBeenInitialized = false;
        this.settings = {
            dropOnlySequencedSyncsOnReseedEnabled: true,
            closeWSBlockedSessionEnabled: true,
            sessionOfflineModeEnabled: true,
            annotationsOrderingEnabled: false,
            deltaOperationsEnabled: false,
            checkIdbByFeatures: false
        };
        this.isDownloaderCompatible = () => {
            if (this.settings.checkIdbByFeatures) {
                if (typeof Array !== 'undefined' && typeof Array.from !== 'undefined' && typeof URL !== 'undefined' && typeof URL.createObjectURL !== 'undefined') {
                    return true;
                }
                return false;
            }
            else {
                if (typeof navigator != 'undefined' && navigator.userAgent) {
                    const ua = navigator.userAgent;
                    if (!ua.indexOf || ua.indexOf('MSIE ') > 0 || ua.indexOf('Trident/') > 0) {
                        // IE browser
                        return false;
                    }
                    else {
                        return true;
                    }
                }
                else {
                    // No userAgent
                    return false;
                }
            }
        };
        this.workerFactory = workerFactory || (() => new WebSocketWorker());
        const defaultSessionManagerFactory = this.createDefaultSessionManagerFactory();
        this.sessionManagerFactory = (...args) => {
            // Use the default if no factory was provided, or the provided one decides not to return an instance
            return (sessionManagerFactory === null || sessionManagerFactory === void 0 ? void 0 : sessionManagerFactory(...args)) || defaultSessionManagerFactory(...args);
        };
        this.sessionFactory = sessionFactory || ((config) => new SessionProxy(config));
    }
    /**
     * Initialize the Runtime.
     * This will succeed, even if we're unable to reach the AugLoop servers,
     * leaving the service in a local-only state.
     */
    init(serviceUrl, clientMetadata, hostCallbacks, options) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        // dim0 = batchOptions; dim1 = hasBeenInit; dim2 = num loggers; dim3 = stack trace
        const op = new LoggingOperation({
            operationName: 'InitRuntime',
            resourceId: serviceUrl,
            dimension1: this.hasBeenInitialized.toString()
        });
        op.start();
        this.hasBeenInitialized = true;
        if (((_a = clientMetadata.appName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) == 'outlook' && ((_b = clientMetadata.appPlatform) === null || _b === void 0 ? void 0 : _b.toLowerCase()) == 'web') {
            if (ClientRuntime.haveCalledInit) {
                const err = new Error('Runtime already initialized');
                op.dimension3 = err.stack;
                op.dimension2 = getNumLoggers(LoggingLevel.info).toString();
                this.logOperation(op, false, 'Error', err.message);
                return Promise.reject(err);
            }
            ClientRuntime.haveCalledInit = true;
            op.dimension3 = new Error().stack; // Not a real error, but want to see the stack trace
        }
        if (options && options.networkMode === NetworkMode.LocalWorkflowsOnly) {
            // No connections to AL service
            this.sessionManagerFactory = () => new EmptySessionManager();
        }
        this.annotationResultsProcessor = new RoutingAnnotationResultsProcessor(() => this.settings.annotationsOrderingEnabled);
        this.inferenceServiceFactory = options.inferenceServiceFactory;
        this.clientMetadata = clientMetadata;
        if (this.clientMetadata) {
            this.clientMetadata.runtimeVersion = getVersion();
        }
        this.hostCallbacks = hostCallbacks;
        Log.addLogger(new TelemetryLogger(this.hostCallbacks));
        op.dimension2 = getNumLoggers(LoggingLevel.info).toString();
        const createOperationLoggingAggregator = (eventName, operationNameField, operationNamesToAggregateOnSuccess, telemetryAggregationIntervalSec) => new LoggingAggregator(eventName, (obj) => obj.success && operationNamesToAggregateOnSuccess.indexOf(obj[operationNameField]) >= 0, 
        // Dimensions
        [
            operationNameField,
            'resourceId',
            'success',
            'resultSignature',
            'dimension0',
            'dimension1',
            'dimension2',
            'dimension3'
        ], 
        // Measures
        ['durationMs'], telemetryAggregationIntervalSec);
        Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', [
            'ExecuteWorkflow',
            'ExecuteBatch',
            'ProcessResponse',
            'LocalDeltaUpdate',
            'ApplyFormattedTextTileDeltaForLocalWorkflows',
            'ApplyTextTileDeltaForLocalWorkflows',
            'FindRangeForDelta',
            'RunModelForInferencing',
            'GetResource',
            'CreateTextTileDeltaFromItem'
        ], (_c = options.telemetryAggregationIntervalSec) !== null && _c !== void 0 ? _c : defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('SessionHealth', 'sessionHealthEventName', [
            'ProcessMessage'
        ], (_d = options.telemetryAggregationIntervalSec) !== null && _d !== void 0 ? _d : defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('Operation', 'operationName', [
            'ExecuteLambda'
        ], (_e = options.telemetryAggregationIntervalSec) !== null && _e !== void 0 ? _e : 2 * defaultAggregatorIntervalSec));
        Log.addAggregator(createOperationLoggingAggregator('SessionHealth', 'sessionHealthEventName', [
            'SendMessage'
        ], (_f = options.telemetryAggregationIntervalSec) !== null && _f !== void 0 ? _f : 2 * defaultAggregatorIntervalSec));
        this.batchOptions = options.batchOptions;
        if (this.batchOptions) {
            op.dimension0 = JSON.stringify(this.batchOptions);
        }
        if (serviceUrl) {
            this.defaultServiceUrl = Utils.convertServiceUrlToWebSocket(serviceUrl);
            this.serviceProtocol = this.defaultServiceUrl.split(':')[0].toLowerCase();
        }
        const promises = [];
        promises.push(this.isFeatureEnabled('CheckIdbByFeatures', false).then((isEnabled) => {
            this.settings.checkIdbByFeatures = isEnabled;
        }));
        promises.push(this.isFeatureEnabled('DropOnlySequencedSyncsOnReseedDisabled').then((isDisabled) => {
            this.settings.dropOnlySequencedSyncsOnReseedEnabled = !isDisabled;
        }));
        promises.push(this.isFeatureEnabled('CloseWSBlockedSessionDisabled').then((isDisabled) => {
            this.settings.closeWSBlockedSessionEnabled = !isDisabled;
        }));
        promises.push(this.isFeatureEnabled('SessionOfflineModeDisabled').then((isDisabled) => {
            this.settings.sessionOfflineModeEnabled = !isDisabled;
        }));
        promises.push(this.isFeatureEnabled('AnnotationsOrderingEnabled', false).then((isEnabled) => {
            this.settings.annotationsOrderingEnabled = isEnabled;
        }));
        const optionsLWEM = { enableDeltas: false, enableEarlyJoin: false };
        promises.push(Promise.all([
            this.isFeatureEnabled('DeltaOperationsEnabled').then((isEnabled) => optionsLWEM.enableDeltas = isEnabled).catch(() => optionsLWEM.enableDeltas = false),
            this.isFeatureEnabled('EarlyJoinCompletionEnabled').then((isEnabled) => optionsLWEM.enableEarlyJoin = isEnabled).catch(() => optionsLWEM.enableEarlyJoin = false)
        ]).then(() => {
            this.localWorkflowManager = new LocalWorkflowExecutionManager(options.modelDownloader && this.isDownloaderCompatible() ? options.modelDownloader : undefined, this.inferenceServiceFactory, optionsLWEM);
        }));
        const flightsChecker = parseFlights((_g = clientMetadata.flights) !== null && _g !== void 0 ? _g : '');
        this.isDeltaGeneratorEnabled = flightsChecker.getBooleanValue('Microsoft.Office.WordOnline.AugloopDeltas', (_h = options.isDeltaGeneratorEnabled) !== null && _h !== void 0 ? _h : false);
        this.disableSyncDeltaSending = flightsChecker.getBooleanValue('Microsoft.Office.WordOnline.DisableSyncDeltaSending', (_j = options.disableSyncDeltaSending) !== null && _j !== void 0 ? _j : false);
        this.syncDeltaTimeout = flightsChecker.getIntValue('Microsoft.Office.WordOnline.SyncDeltaTimeout', options.syncDeltaTimeout);
        return Promise.all(promises)
            .then(() => {
            this.logOperation(op, true);
        })
            .catch((error) => {
            this.logOperation(op, false, 'Error', error ? error.message : '(no error)');
        });
    }
    getServiceProtocol() {
        return this.serviceProtocol;
    }
    registerLocalWorkflow(workflow) {
        this.localWorkflowManager.registerLocalWorkflow(workflow);
    }
    flushTelemetry(stop) {
        Log.flushAggregators(stop);
    }
    createSession(options) {
        var _a;
        const docSessionId = options ? options.docSessionId : undefined;
        const existingSession = this.sessionsByDocSessionId.get(docSessionId);
        if (existingSession && existingSession.isClosed === false) {
            throw new Error('docSessionId already exists');
        }
        let serviceUrl = options ? ((_a = options.serviceUrl) !== null && _a !== void 0 ? _a : this.defaultServiceUrl) : this.defaultServiceUrl;
        serviceUrl = Utils.convertServiceUrlToWebSocket(serviceUrl);
        if (!serviceUrl) {
            // Still allow local workflows to run for this session
            options.networkMode = NetworkMode.LocalWorkflowsOnly;
        }
        // Every session gets its own customized copy of the metadata
        const clientMetadata = Object.assign({}, this.clientMetadata);
        clientMetadata.docSessionId = docSessionId;
        if (options && options.flights) {
            clientMetadata.flights = clientMetadata.flights
                ? clientMetadata.flights + ';' + options.flights
                : options.flights;
        }
        const session = this.sessionFactory({
            hostCallbacks: this.hostCallbacks,
            sessionManager: this.sessionManagerFactory(clientMetadata, serviceUrl, options),
            batchOptions: this.batchOptions,
            extensionConfigs: (options === null || options === void 0 ? void 0 : options.extensionConfigs) || [],
            clientMetadata: clientMetadata,
            localWorkflowManager: options && options.enableComplexLocalWorkflows ? undefined : this.localWorkflowManager,
            annotationResultsProcessor: this.annotationResultsProcessor,
            localRegisteredWorkflows: (options === null || options === void 0 ? void 0 : options.localRegisteredWorkflows) || [],
            enableRemoteExecutionNotification: (options === null || options === void 0 ? void 0 : options.enableRemoteExecutionNotification) || false,
            networkMode: options && options.networkMode !== undefined ? options.networkMode : undefined,
            egress: options ? options.egress : undefined,
            isDeltaGeneratorEnabled: this.isDeltaGeneratorEnabled,
            disableSyncDeltaSending: this.disableSyncDeltaSending,
            syncDeltaTimeout: this.syncDeltaTimeout
        });
        this.sessionsByDocSessionId.set(docSessionId, session);
        if (options && options.onSessionConnect) {
            session.setConnectCallback(options.onSessionConnect);
        }
        if (options && options.onSessionDisconnect) {
            session.setDisconnectCallback(options.onSessionDisconnect);
        }
        if (options && options.onSessionReconnect) {
            session.setReconnectCallback(options.onSessionReconnect);
        }
        if (options && options.onSessionClose) {
            session.setSessionCloseCallback(options.onSessionClose);
        }
        if (options && options.onServerAuthenticationStateChangeCallback) {
            session.setServerAuthenticationStateChangeCallback(options.onServerAuthenticationStateChangeCallback);
        }
        return session.initialize ? session.initialize() : Promise.resolve(session);
    }
    getSessionManagerFactory() {
        return this.sessionManagerFactory;
    }
    createDefaultSessionManagerFactory() {
        return (clientMetadata, sessionServiceUrl, options) => {
            if (options && options.enableComplexLocalWorkflows) {
                // This is only available in the Phase3 bundle
                throw new Error('NYI');
            }
            if (options && options.networkMode == NetworkMode.LocalWorkflowsOnly) {
                // No connections made to AL server, allow local workflows to run
                return new EmptySessionManager();
            }
            const tokenRefreshManager = new TokenRefreshManager();
            const sessionStats = new SessionStats();
            let sendMessage = () => { Log.error(0x222c318f /* tag_8ldgp */, LogCategory.CoreDefault, 'Unexpectedly not set sendMessage'); };
            const sessionInitializer = new SessionInitializer(() => sessionStats, clientMetadata, tokenRefreshManager, (message, onResponse, sendOnlyIfConnected) => { sendMessage(message, onResponse, sendOnlyIfConnected); }, {
                getAuthToken: this.getAuthToken.bind(this, clientMetadata.docSessionId),
                overrideSessionInitMessage: this.hostCallbacks.overrideSessionInitMessage,
                enableRemoteExecutionNotification: (options === null || options === void 0 ? void 0 : options.enableRemoteExecutionNotification) || false
            });
            const sessionManager = new WebSocketSessionManager(Utils.convertServiceUrlToWebSocket(sessionServiceUrl), sessionStats, this.workerFactory, sessionInitializer, clientMetadata, tokenRefreshManager, this.settings);
            sendMessage = sessionManager.sendMessage.bind(sessionManager);
            sessionManager.on('disconnect', () => tokenRefreshManager.clearRefreshTimeouts());
            sessionManager.on('connect', (isSeedingRequired, sessionUrl, origin, authToken) => {
                if (isSeedingRequired && this.hostCallbacks.setSessionData) {
                    this.hostCallbacks.setSessionData(sessionUrl, origin, authToken);
                }
            });
            return sessionManager;
        };
    }
    getAuthToken(docSessionId) {
        const tokenRequest = { Tickets: [] };
        if (docSessionId) {
            tokenRequest.DocSessionId = docSessionId;
        }
        return this.hostCallbacks.requestAuthToken(tokenRequest)
            .then((tokenResponse) => {
            if (!tokenResponse) {
                return undefined;
            }
            // In case we got the token from the host, extract its value
            return tokenResponse.Token;
        })
            .catch(() => {
            return undefined;
        });
    }
    isFeatureEnabled(featureName, defaultValue = false, scope = 'None') {
        return Utils.isFeatureEnabled(this.hostCallbacks, featureName, defaultValue, scope);
    }
    logOperation(op, success, resultSignature, resultDescription) {
        op.stop();
        op.success = success;
        op.resultSignature = resultSignature;
        op.resultDescription = resultDescription;
        Log.info(0x222c3196 /* tag_8ldgw */, LogCategory.CoreDefault, op);
    }
}
export const runtime = new ClientRuntime();
export const createRuntime = () => { return new ClientRuntime(); };
//# sourceMappingURL=runtime-client.js.map